import { BookOpen } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { learnerBookTitle } from '@/utils/learnerBookTitle'
import type { MaterialNotice } from '@/api/lcLearning'

interface Props {
  notices: MaterialNotice[]
}

export function MaterialNoticeBanner({ notices }: Props) {
  const { t } = useTranslation(['learner'])

  return (
    <section
      className="shrink-0 rounded-2xl bg-lc-bg-primary px-5 pb-6 pt-5 shadow-lc-v3-card"
      aria-label={t('materialNotice.aria')}
    >
      <div className="flex items-center gap-2">
        <span className="inline-flex size-8 items-center justify-center rounded-lg bg-lc-key-50 text-lc-key-500">
          <BookOpen className="size-5" aria-hidden="true" />
        </span>
        <h2 className="text-lc-v3-h5 font-semibold text-lc-text-primary">
          {t('materialNotice.title')}
        </h2>
      </div>
      {notices.length > 0 ? (
        <ul className="mt-5 flex items-center gap-6 px-1">
          {notices.map((notice) => {
            const title = learnerBookTitle(notice.title)
            const bookName = notice.volumeLabel && !title.includes(notice.volumeLabel)
              ? `${title} ${notice.volumeLabel}`
              : title
            return (
              <li
                key={`${notice.bookId}-${notice.materialType}`}
                className="flex min-w-0 flex-1 items-center gap-3"
              >
                {notice.learnerCoverImageUrl ? (
                  <img
                    src={notice.learnerCoverImageUrl}
                    alt=""
                    className="h-16 w-[45px] shrink-0 rounded-md border border-black/10 object-contain shadow-[0_2px_8px_0_rgb(0_0_0/0.1)]"
                  />
                ) : (
                  <span className="inline-flex h-16 w-[45px] shrink-0 items-center justify-center rounded-md border border-black/10 bg-lc-key-50 text-lc-key-500 shadow-[0_2px_8px_0_rgb(0_0_0/0.1)]">
                    <BookOpen className="size-5" aria-hidden="true" />
                  </span>
                )}
                <span className="min-w-0">
                  <span className="block text-lc-v3-label-md font-semibold text-lc-v3-gray-600">
                    {t(`materialNotice.type.${notice.materialType}`)}
                  </span>
                  <strong className="mt-1 block truncate text-lc-v3-body-1 font-bold text-lc-text-primary">
                    {bookName}
                  </strong>
                  {notice.startPage != null ? (
                    <span className="sr-only">
                      {t('materialNotice.page', { page: notice.startPage })}
                    </span>
                  ) : null}
                  {notice.message ? (
                    <span className="mt-1 block truncate text-lc-v3-caption-1 text-lc-text-secondary">
                      {notice.message}
                    </span>
                  ) : null}
                </span>
              </li>
            )
          })}
        </ul>
      ) : (
        <div role="status" className="mt-5 flex h-16 items-center gap-3 px-1">
          <span className="inline-flex h-16 w-[45px] shrink-0 items-center justify-center rounded-md border border-lc-disabled-border bg-lc-disabled-bg text-lc-text-disabled">
            <BookOpen className="size-5" aria-hidden="true" />
          </span>
          <p className="text-lc-v3-body-1 font-medium text-lc-text-tertiary">
            {t('materialNotice.empty')}
          </p>
        </div>
      )}
    </section>
  )
}
